import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, SlidersHorizontal, ShieldCheck, AlertTriangle, ExternalLink, GitCompare, Check, X, TrendingDown, ArrowDownWideNarrow } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const CONDITIONS = [
  { v: "", l: "Any" },
  { v: "new", l: "New" },
  { v: "cpo", l: "CPO" },
  { v: "used", l: "Used" },
];

function BadgePill({ value }) {
  const map = {
    "Great Deal": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    "Fair Price": "bg-blue-500/10 text-blue-300 border-blue-500/20",
    "Authorized": "bg-white/5 text-gray-300 border-white/10",
    "Anomaly": "bg-yellow-500/10 text-yellow-300 border-yellow-500/20",
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded border font-medium ${map[value] || map.Authorized}`}>
      {value}
    </span>
  );
}

function DealRatingPill({ market }) {
  const rating = market && market.value ? market.rating : null;
  if (!rating) return null;
  const cls = {
    "Great Deal": "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    "Fair Price": "bg-blue-500/10 text-blue-300 border-blue-500/25",
    "Above Market": "bg-yellow-500/15 text-yellow-300 border-yellow-500/25",
    "Overpriced": "bg-red-500/15 text-red-300 border-red-500/25",
  }[rating] || "bg-white/5 text-gray-300 border-white/10";
  const pct = market.delta_pct != null ? market.delta_pct * 100 : null;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] mt-1.5 px-2 py-0.5 rounded-full border font-bold ${cls}`} data-testid={`deal-rating-${rating.replace(/\s/g, "-").toLowerCase()}`}>
      {rating}{pct != null && <span className="font-mono font-normal opacity-80">{pct <= 0 ? "" : "+"}{pct.toFixed(0)}%</span>}
    </span>
  );
}

function InventoryCard({ v, selected, onToggleCompare }) {
  const hasImage = v.image_urls && v.image_urls.length > 0;
  const img = hasImage ? v.image_urls[0] : null;
  return (
    <Link to={`/inventory/${v.id}`} className="group bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl overflow-hidden hover:border-white/20 transition-all block" data-testid={`inv-card-${v.id}`}>
      <div className="aspect-[16/10] bg-[#050507] relative overflow-hidden">
        {img ? (
          <img src={img} alt={`${v.year} ${v.make} ${v.model}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 text-xs gap-2">
            <AlertTriangle className="w-6 h-6" /> No photos available
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-1.5">
          <BadgePill value={v.badge} />
          <span className="text-[10px] px-2 py-0.5 rounded bg-black/60 text-white border border-white/10 backdrop-blur">
            <ShieldCheck className="w-2.5 h-2.5 inline mr-0.5" /> Authorized
          </span>
        </div>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleCompare(v); }}
          className={`absolute bottom-3 right-3 z-10 text-[10px] font-medium px-2.5 py-1 rounded-full border inline-flex items-center gap-1 backdrop-blur transition-colors ${
            selected ? "bg-emerald-500 text-black border-emerald-400" : "bg-black/60 text-white border-white/20 hover:bg-black/80"
          }`}
          data-testid={`inv-compare-toggle-${v.id}`}
        >
          {selected ? <><Check className="w-3 h-3" /> Added</> : <><GitCompare className="w-3 h-3" /> Compare</>}
        </button>
        {v.is_mocked && (
          <div className="absolute top-3 right-3 text-[9px] px-2 py-0.5 rounded bg-yellow-500/90 text-black font-mono font-bold">
            MOCKED
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="font-display text-lg leading-tight">{v.year} {v.make} {v.model}</div>
            <div className="text-xs text-gray-500 mt-0.5">{v.trim} · {v.condition?.toUpperCase()} · {v.mileage?.toLocaleString()} mi</div>
            <DealRatingPill market={v.market} />
          </div>
          <div className="text-right">
            <div className="font-display text-2xl">${(v.price || 0).toLocaleString()}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Asking</div>
          </div>
        </div>
        <div className="text-xs text-gray-400 border-t border-white/5 pt-3 mt-3 flex items-center justify-between">
          <span>{v.dealer_name}</span>
          <span className="text-gray-500">{v.location}</span>
        </div>
      </div>
    </Link>
  );
}

export default function AuthorizedInventory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ search: "", make: "", condition: "", maxPrice: 150000 });
  const [compare, setCompare] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [onlyDeals, setOnlyDeals] = useState(false);
  const [bestFirst, setBestFirst] = useState(false);

  const toggleCompare = (v) => {
    setCompare((cur) => {
      if (cur.find((c) => c.id === v.id)) return cur.filter((c) => c.id !== v.id);
      if (cur.length >= 3) return cur;
      return [...cur, v];
    });
  };
  const compareIds = new Set(compare.map((c) => c.id));

  useEffect(() => {
    setLoading(true);
    const params = {
      search: state.search || undefined,
      make: state.make || undefined,
      condition: state.condition || undefined,
      max_price: state.maxPrice !== 150000 ? state.maxPrice : undefined,
    };
    Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);
    api.get("/authorized-inventory", { params })
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  }, [state]);

  const brands = useMemo(() => Array.from(new Set(rows.map((r) => r.make))).sort(), [rows]);
  const anyMocked = rows.some((r) => r.is_mocked);
  const belowMarket = (r) => r.market && r.market.value && r.market.delta_pct < 0;
  const dealCount = useMemo(() => rows.filter(belowMarket).length, [rows]);
  const displayRows = useMemo(() => {
    let out = onlyDeals ? rows.filter(belowMarket) : rows.slice();
    if (bestFirst) {
      const key = (r) => (r.market && r.market.value != null ? r.market.delta_pct : Infinity);
      out.sort((a, b) => key(a) - key(b));
    }
    return out;
  }, [rows, onlyDeals, bestFirst]);

  return (
    <div className="max-w-[1500px] mx-auto px-5 lg:px-10 py-12 md:py-16" data-testid="authorized-inventory-page">
      <div className="mb-8">
        <p className="label-mono mb-3">Authorized Dealer Inventory</p>
        <h1 className="font-display text-4xl md:text-6xl font-light tracking-tighter">Real cars. Real dealers.</h1>
        <p className="text-gray-400 max-w-2xl mt-3">
          Live inventory pulled directly from authorized dealer feeds. Every listing is VIN-verified, price-benchmarked against comparable trims, and passes our anomaly checks before it goes live.
        </p>
      </div>

      {anyMocked && (
        <div className="mb-6 p-4 rounded-2xl bg-yellow-500/5 border border-yellow-500/20 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-200/90">
            <span className="font-medium">Preview data.</span> This is a mocked feed for demo purposes only — no live provider is connected yet.
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-6 mb-8">
        <div className="lg:col-span-6 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search make, model, trim, dealer…"
            value={state.search}
            onChange={(e) => setState((s) => ({ ...s, search: e.target.value }))}
            className="bg-[#0A0A0E] border-[#1A1A24] text-white pl-9 h-12 rounded-full"
            data-testid="inv-search-input"
          />
        </div>
        <div className="lg:col-span-3">
          <select
            value={state.make}
            onChange={(e) => setState((s) => ({ ...s, make: e.target.value }))}
            className="w-full h-12 rounded-full bg-[#0A0A0E] border border-[#1A1A24] text-white px-5 text-sm"
            data-testid="inv-make-select"
          >
            <option value="">All makes</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="lg:col-span-3">
          <select
            value={state.condition}
            onChange={(e) => setState((s) => ({ ...s, condition: e.target.value }))}
            className="w-full h-12 rounded-full bg-[#0A0A0E] border border-[#1A1A24] text-white px-5 text-sm"
            data-testid="inv-condition-select"
          >
            {CONDITIONS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm text-gray-400" data-testid="inv-count">{loading ? "Loading…" : `${displayRows.length} authorized vehicles`}</p>
          <button
            onClick={() => setOnlyDeals((v) => !v)}
            className={`text-xs px-3 py-1.5 rounded-full border font-medium inline-flex items-center gap-1.5 transition-colors ${onlyDeals ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "border-white/10 text-gray-300 hover:bg-white/5"}`}
            data-testid="inv-chip-great-deals"
          >
            <TrendingDown className="w-3.5 h-3.5" /> Great Deals{dealCount ? ` (${dealCount})` : ""}
          </button>
          <button
            onClick={() => setBestFirst((v) => !v)}
            className={`text-xs px-3 py-1.5 rounded-full border font-medium inline-flex items-center gap-1.5 transition-colors ${bestFirst ? "bg-white/10 text-white border-white/30" : "border-white/10 text-gray-300 hover:bg-white/5"}`}
            data-testid="inv-sort-best-deals"
          >
            <ArrowDownWideNarrow className="w-3.5 h-3.5" /> Best deals first
          </button>
        </div>
        <Link to="/lease-deals" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1">
          Looking for lease deals? <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="aspect-[5/6] bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : displayRows.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-white/10 rounded-2xl px-6" data-testid="inv-empty">
          <SlidersHorizontal className="w-8 h-8 text-gray-600 mx-auto mb-3" />
          <div className="font-display text-2xl mb-3">{onlyDeals ? "No below-market deals right now." : "No authorized inventory matches your filters."}</div>
          <p className="text-gray-400 max-w-lg mx-auto mb-7">{onlyDeals ? "Turn off the Great Deals filter to see all inventory, or let our broker team hunt one down." : "Try widening your search, or let our broker team source it directly."}</p>
          <div className="flex flex-wrap justify-center gap-3">
            {onlyDeals && <button onClick={() => setOnlyDeals(false)} className="px-6 py-3 rounded-full border border-white/15 text-sm hover:bg-white/5" data-testid="inv-clear-deals">Show all inventory</button>}
            <Link to="/find-my-car" className="px-6 py-3 rounded-full silver-gradient text-[#050507] font-bold text-sm">Request This Vehicle</Link>
            <Link to="/lease-deals" className="px-6 py-3 rounded-full border border-white/15 text-sm hover:bg-white/5">Browse Lease Deals</Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="inv-grid">
          {displayRows.map((v) => <InventoryCard key={v.id} v={v} selected={compareIds.has(v.id)} onToggleCompare={toggleCompare} />)}
        </div>
      )}

      <p className="text-[11px] text-gray-500 mt-12 max-w-3xl">
        Authorized inventory is provided by licensed dealer partners and refreshed continuously. Prices reflect the dealer&apos;s asking price and are subject to change, tax, title, and fees. Verified via VIN decoding (NHTSA) and rolling median price checks.
      </p>

      {/* Floating compare tray */}
      {compare.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 px-4 pb-4 pointer-events-none" data-testid="compare-tray">
          <div className="max-w-3xl mx-auto pointer-events-auto bg-[#0A0A0E]/95 backdrop-blur border border-white/15 rounded-2xl p-3 flex items-center gap-3 shadow-2xl">
            <div className="flex -space-x-3">
              {compare.map((c) => (
                <div key={c.id} className="relative w-14 h-11 rounded-lg overflow-hidden border-2 border-[#0A0A0E] bg-[#050507]">
                  {c.image_urls?.[0] ? <img src={c.image_urls[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full" />}
                  <button
                    onClick={() => toggleCompare(c)}
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-black border border-white/30 flex items-center justify-center"
                    data-testid={`compare-remove-${c.id}`}
                  >
                    <X className="w-2.5 h-2.5 text-white" />
                  </button>
                </div>
              ))}
            </div>
            <div className="text-xs text-gray-400 hidden sm:block flex-1">
              {compare.length}/3 selected {compare.length < 2 && <span className="text-gray-600">· add one more to compare</span>}
            </div>
            <button onClick={() => setCompare([])} className="text-xs text-gray-400 hover:text-white px-3 py-2" data-testid="compare-clear">Clear</button>
            <button
              onClick={() => setShowCompare(true)}
              disabled={compare.length < 2}
              className="px-5 py-2.5 rounded-full silver-gradient text-[#050507] font-bold text-sm disabled:opacity-40 inline-flex items-center gap-2"
              data-testid="compare-open-btn"
            >
              <GitCompare className="w-4 h-4" /> Compare
            </button>
          </div>
        </div>
      )}

      <CompareModal open={showCompare} onClose={() => setShowCompare(false)} vehicles={compare} />
    </div>
  );
}

function CompareModal({ open, onClose, vehicles }) {
  const rows = [
    { label: "Price", fmt: (v) => `$${(v.price || 0).toLocaleString()}`, best: (vs) => Math.min(...vs.map((v) => v.price || Infinity)) },
    { label: "Mileage", fmt: (v) => `${(v.mileage || 0).toLocaleString()} mi`, best: (vs) => Math.min(...vs.map((v) => v.mileage ?? Infinity)) },
    { label: "Deal Score", fmt: (v) => v.badge || "Authorized" },
    { label: "vs Market", fmt: (v) => (v.price_delta_pct != null ? `${(v.price_delta_pct * 100).toFixed(1)}%` : "—") },
    { label: "Condition", fmt: (v) => (v.condition || "").toUpperCase() },
    { label: "Year", fmt: (v) => v.year },
    { label: "Dealer", fmt: (v) => v.dealer_name || "—" },
    { label: "Location", fmt: (v) => v.location || "—" },
  ];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0A0A0E] border-[#1A1A24] text-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="compare-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light">Side-by-side comparison</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">Compare price, mileage and deal score across the vehicles you selected.</DialogDescription>
        </DialogHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="w-28"></th>
                {vehicles.map((v) => (
                  <th key={v.id} className="p-2 align-bottom text-left">
                    <div className="aspect-[16/10] rounded-lg overflow-hidden bg-[#050507] mb-2 border border-white/10">
                      {v.image_urls?.[0] ? <img src={v.image_urls[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-600">NO PHOTO</div>}
                    </div>
                    <Link to={`/inventory/${v.id}`} className="font-medium hover:underline block leading-tight">{v.year} {v.make} {v.model}</Link>
                    <div className="text-xs text-gray-500 font-normal">{v.trim}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const bestVal = r.best ? r.best(vehicles) : null;
                return (
                  <tr key={r.label} className="border-t border-white/5">
                    <td className="p-2 text-[10px] uppercase tracking-wider text-gray-500">{r.label}</td>
                    {vehicles.map((v) => {
                      const isBest = r.best && (r.label === "Price" ? v.price === bestVal : v.mileage === bestVal);
                      return (
                        <td key={v.id} className={`p-2 ${isBest ? "text-emerald-300 font-semibold" : "text-gray-200"}`} data-testid={`compare-cell-${r.label.toLowerCase().replace(/\s/g,'-')}-${v.id}`}>
                          {r.fmt(v)}{isBest && <span className="ml-1 text-[9px] uppercase tracking-wider text-emerald-500/70">best</span>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-gray-500 mt-2">Deal score and vs-market delta come from our VIN + rolling-median price checks. Lowest price and mileage are highlighted.</p>
      </DialogContent>
    </Dialog>
  );
}
